import { words } from '../ui/words';
import type { EngraveSession } from './EngraveSession';
import { fontStack, loadFont, SYMBOL_FAMILY } from './fonts';
import { inkById, INK_SHADOW_EM } from './inks';
import { baseline } from './layout';
import { LOGO_PATHS, LOGO_VIEWBOX } from './logo';
import { FADE_FROM, hingeImageBox, templeScene, VIEW_RATIO, type HingePlacement, type Scene } from './scene';
import hingeUrl from '../../assets-source/hinge/hinge-nickel.webp?url';
import { isEditable, slotFace, slotSide, type EngraveState, type SlotKey, type TempleFace, type TempleSide } from './state';
import { templeImage, type TempleImage } from './templeImage';

/**
 * A110Z3 — the engraved temples for the workshop (David : « quand ça marque Je la veux, je veux que ça prenne
 * les infos de ce que la personne a choisi plus les photos des branches pour connaître le placement et la
 * couleur »).
 *
 * A sheet drawn on a canvas, appended under the « Je la veux » photo: one row per engraved view (the logo inside
 * the left temple, then each written text), each with its caption (« Branche droite · intérieur — « Bali 🌴 » ·
 * lettres 2,5 mm · centre à 24 mm de la charnière ») and the temple exactly as the try-on shows it — the photo in
 * the chosen acetate, the hinge inside, the text in its typeface, colour, size, place and tilt. Same geometry as
 * the cards (`templeScene`), so what the customer placed is what the workshop sees.
 */
export interface SheetWords {
  title: string;
  /** « Helvetica · Blanc ». */
  subtitle: string;
  note: string;
  view: (side: TempleSide, face: TempleFace) => string;
  /** Caption of a row after the view name: the text and its details (null for the logo row). */
  caption: (slot: SlotKey) => string;
}

export interface SheetInput {
  templeId: string;
  image: TempleImage | null;
  state: EngraveState;
  words: SheetWords;
  /** UI typeface (the captions). */
  family: string;
  width?: number;
  /** A201: the rendered hinge picture (`loadHingeArt`); without it the inner views have no hinge. */
  hinge?: CanvasImageSource | null;
  /** A202: the four views always (an empty face says so), not only the engraved ones. */
  all?: boolean;
}

export const SHEET = {
  width: 1080,
  margin: 72,
  top: 64,
  bottom: 56,
  /** Title, subtitle, then a gap before the rows. */
  titleSize: 22, subtitleSize: 25, headGap: 40,
  captionSize: 24, captionGap: 14, rowGap: 34,
  noteSize: 20,
  paper: '#f6f2ec', card: '#ebe5dc', ink: '#1d1d1b', muted: '#5b5750', rule: 'rgba(29, 29, 27, .14)',
} as const;

/** Views in workshop order: left temple inside / outside, right temple inside / outside. */
const ORDER: readonly SlotKey[] = ['il', 'el', 'ir', 'er'];

/** The views carrying an engraving: the logo always, each text that is not empty. */
export function sheetSlots(state: EngraveState): SlotKey[] {
  return ORDER.filter(s => !isEditable(s) || state.texts[s] !== '');
}

type Ctx = CanvasRenderingContext2D;
type Source = CanvasImageSource;

/** Rows laid out: their scene and the drawing height at the sheet width. */
function rows(input: SheetInput, inner: number): Array<{ slot: SlotKey; scene: Scene; h: number }> {
  return (input.all ? ORDER : sheetSlots(input.state)).map(slot => {
    const scene = templeScene({ templeId: input.templeId, side: slotSide(slot), face: slotFace(slot), image: input.image, state: input.state, ratio: VIEW_RATIO });
    return { slot, scene, h: Math.round((inner * scene.view.h) / scene.view.w) };
  });
}

/** Caption lines no wider than `max` (greedy, by words). */
function wrap(ctx: Ctx, text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(next).width > max) { out.push(line); line = word; } else line = next;
  }
  if (line) out.push(line);
  return out;
}

/** The sheet, or null when the canvas is unavailable. Fonts must be loaded (the caller waits for them). */
export function renderEngravingSheet(input: SheetInput): HTMLCanvasElement | null {
  const S = SHEET;
  const width = input.width ?? S.width;
  const inner = width - 2 * S.margin;
  const laid = rows(input, inner);
  if (!laid.length) return null;
  const captionFont = `500 ${S.captionSize}px ${SYMBOL_FAMILY}, ${input.family}`;
  // Measure captions first (they may wrap).
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) return null;
  probe.font = captionFont;
  const captions = laid.map(r => {
    const head = input.words.view(slotSide(r.slot), slotFace(r.slot));
    const rest = input.words.caption(r.slot);
    return wrap(probe, rest ? `${head} — ${rest}` : head, inner);
  });
  const lineH = Math.round(S.captionSize * 1.3);
  const headH = S.titleSize + 18 + S.subtitleSize + S.headGap;
  const rowsH = laid.reduce((sum, r, i) => sum + captions[i]!.length * lineH + S.captionGap + r.h + (i < laid.length - 1 ? S.rowGap : 0), 0);
  const height = S.top + headH + rowsH + 40 + S.noteSize + S.bottom;

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = S.paper;
  ctx.fillRect(0, 0, width, height);
  // Rule between the photo band and the sheet.
  ctx.fillStyle = S.rule;
  ctx.fillRect(S.margin, 0, inner, 2);

  let y = S.top;
  ctx.fillStyle = S.ink;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `600 ${S.titleSize}px ${input.family}`;
  spaced(ctx, input.words.title.toUpperCase(), S.margin, y + S.titleSize, 4);
  y += S.titleSize + 18;
  ctx.font = `400 ${S.subtitleSize}px ${SYMBOL_FAMILY}, ${input.family}`;
  ctx.fillText(input.words.subtitle, S.margin, y + S.subtitleSize, inner);
  y += S.subtitleSize + S.headGap;

  laid.forEach((r, i) => {
    ctx.font = captionFont;
    ctx.fillStyle = S.ink;
    for (const line of captions[i]!) { y += lineH; ctx.fillText(line, S.margin, y - Math.round(lineH * 0.22), inner); }
    y += S.captionGap;
    drawRow(ctx, r.scene, input, S.margin, y, inner, r.h);
    y += r.h + (i < laid.length - 1 ? S.rowGap : 0);
  });

  y += 40;
  ctx.font = `400 ${S.noteSize}px ${input.family}`;
  ctx.fillStyle = S.muted;
  ctx.fillText(input.words.note, S.margin, y + S.noteSize * 0.8, inner);
  return canvas;
}

/**
 * A201 (David : « Je la veux envoie les 4 faces des branches avec les textes ») — page 2 of the order sheet (PDF),
 * A4 at 150 dpi: the four views always, in workshop order (left inside, left outside, right inside, right outside),
 * each with its name, what is engraved there (or « rien à graver »), the typeface, colour and letter height, and
 * where it is in millimetres — then the temple exactly as the try-on shows it.
 */
export interface WorkshopPageInput {
  templeId: string;
  image: TempleImage | null;
  state: EngraveState;
  family: string;
  title: string;
  /** « Airmax — Honey · Helvetica ». */
  subtitle: string;
  code: string;
  codeLabel: string;
  note: string;
  view: (side: TempleSide, face: TempleFace) => string;
  /** The lines under a view's name. */
  lines: (slot: SlotKey) => string[];
  /** A201: the rendered hinge picture (`loadHingeArt`). */
  hinge?: CanvasImageSource | null;
}

export const PAGE = { width: 1240, height: 1754, margin: 84 } as const;
const WORKSHOP_ORDER: readonly SlotKey[] = ['il', 'el', 'ir', 'er'];

export function renderWorkshopPage(input: WorkshopPageInput): HTMLCanvasElement | null {
  const S = SHEET;
  const { width, height, margin } = PAGE;
  const inner = width - 2 * margin;
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = S.paper;
  ctx.fillRect(0, 0, width, height);
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  // Head: MAISON MATA · title, the code on the right.
  let y = 96;
  ctx.fillStyle = S.ink;
  ctx.font = `600 22px ${input.family}`;
  spaced(ctx, 'MAISON MATA', margin, y, 6);
  ctx.textAlign = 'right';
  ctx.fillStyle = S.muted;
  ctx.font = `500 17px ${input.family}`;
  ctx.fillText(input.codeLabel.toUpperCase(), width - margin, y - 26);
  ctx.fillStyle = S.ink;
  ctx.font = `600 28px ${input.family}`;
  ctx.fillText(input.code, width - margin, y + 6);
  ctx.textAlign = 'left';
  y += 44;
  ctx.fillStyle = S.rule;
  ctx.fillRect(margin, y, inner, 2);
  y += 58;
  ctx.fillStyle = S.ink;
  ctx.font = `600 34px ${input.family}`;
  ctx.fillText(input.title, margin, y, inner);
  y += 42;
  ctx.font = `400 23px ${SYMBOL_FAMILY}, ${input.family}`;
  ctx.fillStyle = S.muted;
  ctx.fillText(input.subtitle, margin, y, inner);
  y += 34;

  // Rows: sized so that the four fit the page.
  const scenes = WORKSHOP_ORDER.map(slot => templeScene({ templeId: input.templeId, side: slotSide(slot), face: slotFace(slot), image: input.image, state: input.state, ratio: VIEW_RATIO }));
  const headFont = `600 22px ${input.family}`;
  const lineFont = `400 21px ${SYMBOL_FAMILY}, ${input.family}`;
  const lineH = 29;
  ctx.font = lineFont;
  const captions = WORKSHOP_ORDER.map(slot => input.lines(slot).flatMap(l => wrap(ctx, l, inner)));
  const footer = 70;
  const captionsH = captions.reduce((sum, c) => sum + lineH * (1 + c.length) + 12, 0);
  const gaps = 30 * (WORKSHOP_ORDER.length - 1) + 24;
  const avail = height - y - footer - captionsH - gaps;
  const natural = scenes.map(sc => (inner * sc.view.h) / sc.view.w);
  const k = Math.min(1, avail / natural.reduce((a, b) => a + b, 0));
  const drawW = inner * k;

  WORKSHOP_ORDER.forEach((slot, i) => {
    y += 24;
    ctx.font = headFont;
    ctx.fillStyle = S.ink;
    ctx.fillText(input.view(slotSide(slot), slotFace(slot)), margin, y);
    ctx.font = lineFont;
    for (const line of captions[i]!) { y += lineH; ctx.fillStyle = line === captions[i]![0] ? S.ink : S.muted; ctx.fillText(line, margin, y, inner); }
    y += 12;
    const h = Math.round(natural[i]! * k);
    drawRow(ctx, scenes[i]!, { templeId: input.templeId, image: input.image, state: input.state, words: undefined as never, family: input.family, hinge: input.hinge ?? null }, margin, y, drawW, h);
    y += h + (i < WORKSHOP_ORDER.length - 1 ? 30 : 0);
  });

  ctx.font = `400 18px ${input.family}`;
  ctx.fillStyle = S.muted;
  for (const [i, line] of wrap(ctx, input.note, inner).entries()) ctx.fillText(line, margin, height - 56 + i * 24, inner);
  return canvas;
}

/** Text with letter spacing, left-aligned (glyph by glyph). */
function spaced(ctx: Ctx, text: string, x: number, y: number, spacing: number): void {
  for (const g of Array.from(text)) { ctx.fillText(g, x, y); x += ctx.measureText(g).width + spacing; }
}

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** One view: card, temple photo (faded far end), hinge, logo or text — in the scene's image pixels. */
function drawRow(ctx: Ctx, sc: Scene, input: SheetInput, x: number, y: number, w: number, h: number): void {
  const k = w / sc.view.w;
  ctx.save();
  roundRect(ctx, x, y, w, h, 18);
  ctx.fillStyle = SHEET.card;
  ctx.fill();
  ctx.clip();
  const toView = () => { ctx.translate(x, y); ctx.scale(k, k); ctx.translate(-sc.view.x, -sc.view.y); };

  // Photo, faded towards the far end like the cards (drawn on its own layer so the fade only touches it).
  const bitmap = input.image?.bitmap ?? null;
  if (bitmap) {
    const layer = document.createElement('canvas');
    layer.width = Math.max(1, Math.round(w)); layer.height = Math.max(1, Math.round(h));
    const lc = layer.getContext('2d');
    if (lc) {
      lc.imageSmoothingQuality = 'high';
      lc.scale(k, k);
      lc.translate(-sc.view.x, -sc.view.y);
      if (sc.mirrored) { lc.translate(sc.W, 0); lc.scale(-1, 1); }
      lc.drawImage(bitmap as Source, 0, 0, sc.W, sc.H);
      lc.setTransform(1, 0, 0, 1, 0, 0);
      const g = lc.createLinearGradient(sc.mirrored ? 0 : layer.width, 0, sc.mirrored ? layer.width : 0, 0);
      g.addColorStop(0, '#000'); g.addColorStop(FADE_FROM, '#000'); g.addColorStop(1, 'rgba(0,0,0,0)');
      lc.globalCompositeOperation = 'destination-in';
      lc.fillStyle = g;
      lc.fillRect(0, 0, layer.width, layer.height);
      ctx.drawImage(layer, x, y);
    }
    layer.width = 0; layer.height = 0;
  }

  ctx.save();
  toView();
  if (sc.hinge) drawHinge(ctx, sc.hinge, input.hinge ?? null);
  const ink = inkById(input.state.inks[sc.slot]);
  if (sc.logo) {
    const l = sc.logo;
    const vb = LOGO_VIEWBOX.split(' ').map(Number) as [number, number, number, number];
    const s = l.height / vb[3];
    const paths = LOGO_PATHS.map(d => new Path2D(d));
    const draw = (dy: number, colour: string) => {
      ctx.save();
      ctx.translate(l.cx, l.cy); ctx.rotate((l.angleDeg * Math.PI) / 180); ctx.translate(-l.cx, -l.cy);
      ctx.translate(l.x, l.y + dy); ctx.scale(s, s); ctx.translate(-vb[0], -vb[1]);
      ctx.fillStyle = colour;
      for (const p of paths) ctx.fill(p, 'evenodd');
      ctx.restore();
    };
    draw(l.height * INK_SHADOW_EM * 1.6, ink.shadow);
    draw(0, ink.color);
  }
  if (sc.text && sc.shown) {
    const t = sc.caption ?? sc.text;
    const b = baseline(t);
    ctx.save();
    ctx.translate(t.cx, t.cy); ctx.rotate((t.angleDeg * Math.PI) / 180); ctx.translate(-t.cx, -t.cy);
    ctx.font = `${sc.font.weight} ${t.fontSize.toFixed(2)}px ${fontStack(sc.font)}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const spacing = sc.font.letterSpacing * t.fontSize;
    const c = ctx as Ctx & { letterSpacing?: string };
    // Same letter spacing as the card's SVG text (both centre the run with its spacing).
    if ('letterSpacing' in c) c.letterSpacing = `${spacing.toFixed(2)}px`;
    ctx.fillStyle = ink.shadow;
    ctx.fillText(sc.shown, b.x, b.y + t.fontSize * INK_SHADOW_EM);
    ctx.fillStyle = ink.color;
    ctx.fillText(sc.shown, b.x, b.y);
    ctx.restore();
  }
  ctx.restore();
  ctx.restore();
}

/** A201: the nickel riveted hinge inside the temple (David's photo, modelled in 3D), drawn as the cards draw it. */
function drawHinge(ctx: Ctx, p: HingePlacement, art: Source | null): void {
  if (!art) return;
  const b = hingeImageBox(p);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate((p.angle * Math.PI) / 180);
  ctx.scale(p.sx, p.sy);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(art, b.x, b.y, b.w, b.h);
  ctx.restore();
}

let hingeArt: Promise<Source | null> | null = null;
/** The rendered hinge picture, decoded once (null if it cannot load: the sheet is drawn without it). */
export function loadHingeArt(): Promise<Source | null> {
  if (!hingeArt) {
    hingeArt = new Promise<Source | null>(resolve => {
      if (typeof Image === 'undefined') { resolve(null); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img as Source);
      img.onerror = () => { hingeArt = null; resolve(null); };
      img.src = hingeUrl;
    });
  }
  return hingeArt;
}

// ------------------------------------------------------------ from the session

/** Waits for a font face, never longer than `ms`. */
async function settle(p: Promise<unknown>, ms: number): Promise<void> {
  await Promise.race([p.catch(() => undefined), new Promise(resolve => window.setTimeout(resolve, ms))]);
}

/**
 * The sheet under the « Je la veux » photo: the temple in the acetate chosen in Couleurs, the engraving font and the
 * symbols loaded first. A202 (David, 11 octobre : « j'ai besoin d'avoir les photos des 4 faces de branche et les
 * textes en plus de la photo client ») : as soon as the engraving differs from the house one, the four faces — an
 * empty face saying « rien à graver » — each with its text, colour, letter height and place. Null while the
 * engraving is the house one (the order sheet PDF still has the four faces) or without a temple photo.
 */
export async function engravingSheet(session: EngraveSession, family: string): Promise<HTMLCanvasElement | null> {
  if (!session.frame.id) return null;
  const image = await templeImage(session.frame.id, session.acetate);
  if (!image) return null;
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  await settle(Promise.all([
    loadFont(session.font),
    fonts ? fonts.load(`500 24px ${family}`) : Promise.resolve(),
    fonts ? fonts.load(`600 22px ${family}`) : Promise.resolve(),
  ]), 3000);
  const w = words().engrave;
  const state = session.state;
  const facts = session.facts();
  // Decimal commas where the UI language writes them (« 2,5 mm »).
  const comma = w.mm(1.5).includes(',');
  const decimals = (t: string) => (comma ? t.replace(/(\d)\.(\d)/g, '$1,$2') : t);
  const caption = (slot: SlotKey): string => {
    const text = isEditable(slot) ? state.texts[slot] : null;
    if (text === '') return w.sheetEmpty;
    const cap = facts.capsMm?.[slot] ?? (isEditable(slot) ? state.style[slot].cap : state.logo.cap);
    const parts = [text === null ? [w.faceLogo, facts.payload.logo.suffix].filter(Boolean).join(' · ') : `« ${text} »`, `${w.colour} ${w.inks[facts.inks[slot].id]}`, w.capOrder(w.mm(cap))];
    const place = facts.placesMm?.[slot];
    if (place) parts.push(decimals(w.sheetPlace(place.x, place.y, place.rot)));
    return parts.join(' · ');
  };
  const hinge = await loadHingeArt();
  return renderEngravingSheet({
    templeId: session.frame.id,
    image,
    hinge,
    state,
    family,
    all: true,
    words: {
      title: w.sheetTitle,
      subtitle: `${session.frame.model} · ${w.font} ${facts.font.name}`,
      note: w.sheetNote,
      view: w.sheetView,
      caption,
    },
  });
}

/**
 * A201: page 2 of the order sheet from the session — the four views of the current engraving (the house one too),
 * the temple in the acetate chosen in Couleurs, the fonts loaded first.
 */
export async function workshopPage(session: EngraveSession, family: string, code: string): Promise<HTMLCanvasElement | null> {
  if (!session.frame.id) return null;
  const image = await templeImage(session.frame.id, session.acetate).catch(() => null);
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  await settle(Promise.all([
    loadFont(session.font),
    fonts ? fonts.load(`600 22px ${family}`) : Promise.resolve(),
    fonts ? fonts.load(`400 21px ${family}`) : Promise.resolve(),
  ]), 3000);
  const all = words();
  const w = all.engrave;
  const o = all.order;
  const facts = session.facts();
  const state = session.state;
  // Decimal commas where the UI language writes them (« 2,5 mm »).
  const comma = w.mm(1.5).includes(',');
  const decimals = (t: string) => (comma ? t.replace(/(\d)\.(\d)/g, '$1,$2') : t);
  const lines = (slot: SlotKey): string[] => {
    const text = isEditable(slot) ? state.texts[slot] : null;
    if (text === '') return [w.sheetEmpty];
    const ink = w.inks[facts.inks[slot].id];
    const cap = facts.capsMm?.[slot] ?? (isEditable(slot) ? state.style[slot].cap : state.logo.cap);
    const out = [text === null ? [w.faceLogo, facts.payload.logo.suffix].filter(Boolean).join(' · ') : `« ${text} »`];
    out.push(text === null
      ? `${w.colour} ${ink} · ${w.capOrder(w.mm(cap))}`
      : `${w.font} ${facts.font.name} · ${w.colour} ${ink} · ${w.capOrder(w.mm(cap))}`);
    const place = facts.placesMm?.[slot];
    if (place) out.push(`${decimals(w.sheetPlace(place.x, place.y, place.rot))}${facts.movedByHand?.includes(slot) ? ` · ${w.moved}` : ''}`);
    return out;
  };
  const hinge = await loadHingeArt();
  return renderWorkshopPage({
    templeId: session.frame.id,
    image,
    hinge,
    state,
    family,
    title: o.pdfFaces,
    subtitle: `${session.frame.model} — ${session.frame.colorway} · ${w.font} ${facts.font.name}`,
    code,
    codeLabel: o.code,
    note: w.sheetNote,
    view: w.sheetView,
    lines,
  });
}
